// src/pages/HomePage.js (or .jsx)
import React from 'react';
import heroBgImage from '../assets/images/hero-background.jpg';
import glows from '../assets/images/glows.gif';
import icon1 from '../assets/images/icon1.png';
import icon2 from '../assets/images/icon2.png';
import icon3 from '../assets/images/icon3.png';
import icon4 from '../assets/images/icon4.png';
import World from '../assets/images/illustration.svg';
import LogoScroller from '../components/LogoScroller'; // Import the new component
import Usecase from '../components/UseCase';
import ContactForm from '../components/ContactForm';
import FaqSection from '../components/FaqSection';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PartnerSection from '../components/PartnerSection';
import AnimatedSection, {
    fadeIn,
    fadeInUp,
    fadeInLeft,
    fadeInRight,
    scaleUp,
    staggerItemFadeInUp
} from '../components/AnimatedSection'; // Adjust path if needed

// --- Animation Variants for Hero Text ---
const heroContentContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.3,
            delayChildren: 0.1,
        },
    },
};
const headingLineContainerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.3,
        },
    },
};
const lineWordContainerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.1,
        },
    },
};
const wordVariants = {
    hidden: {
        opacity: 0,
        y: 20,
        filter: "blur(4px)",
        skewX: -10,
    },
    visible: {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        skewX: 0,
        transition: { type: "spring", damping: 12, stiffness: 100, mass: 0.8 },
    },
};
const heroItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: "easeOut" },
    },
};
// --- End Animation Variants ---

function HomePage() {
    const solutionCards = [
        { icon: icon1, title: 'BloxBlueprint', desc: 'BPModeler enables intuitive, seamless blockchain business process modeling, visualization, and execution by extending BPMN 2.0 standards with key Web3 artifacts. It cuts costs, greatly boosts operational agility, ensures robust compliance, and enhances overall process transparency.', linkTo: "/solutions#bpmn" },
        { icon: icon2, title: 'BPSC Compiler', desc: 'BPSC Compiler automates compliance by converting BPMN 2.0 business processes directly into secure, executable smart contracts. This significantly reduces operational costs, effectively mitigates compliance risks, and enhances transparency and trust.', linkTo: "/solutions#bpsc" },
        { icon: icon3, title: 'FrontierBlox Engine', desc: 'FrontierBlox Engine provides real-time blockchain security and compliance using active monitoring, policy enforcement, and fraud detection. It cuts costs, bolsters security against diverse threats, ensures auditable operations, and fosters vital regulatory confidence.', linkTo: "/solutions#frontierbox" },
        { icon: icon4, title: 'Universal Adapters', desc: 'ZenithBlox s Universal Adapters use pre-built connectors to seamlessly integrate legacy systems with diverse blockchains. This slashes costs, boosts operational agility, ensures compliance, and speeds solution deployment, delivering a significant competitive edge.', linkTo: "/solutions#adapters" },
    ];

    const headingLine1 = "Cut Complexity".split(" ");
    const headingLine2 = "Unlock Innovation".split(" ");

    return (
        <>
            {/* Hero Section */}
            <div
                className="relative bg-cover bg-center text-white pt-24 pb-16 sm:pt-32 sm:pb-20 md:pt-40 md:pb-28 lg:pt-48 lg:pb-32"
                style={{ backgroundImage: `url(${heroBgImage})` }}
            >
                <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                <motion.div
                    className="relative container mx-auto px-4 sm:px-6 lg:px-8 z-10 w-full md:w-5/6 lg:w-2/3 text-left"
                    variants={heroContentContainerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <motion.h1
                        className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-tight mb-6"
                        variants={headingLineContainerVariants}
                    >
                        <motion.span className="block" variants={lineWordContainerVariants}>
                            {headingLine1.map((word, index) => (
                                <motion.span
                                    key={`${word}-${index}-line1`}
                                    variants={wordVariants}
                                    className="inline-block mr-2 md:mr-4"
                                >
                                    {word}
                                </motion.span>
                            ))}
                        </motion.span>
                        <motion.span className="block" variants={lineWordContainerVariants}>
                            {headingLine2.map((word, index) => (
                                <motion.span
                                    key={`${word}-${index}-line2`}
                                    variants={wordVariants}
                                    className="inline-block mr-2 md:mr-4"
                                >
                                    {word}
                                </motion.span>
                            ))}
                        </motion.span>
                    </motion.h1>
                    <motion.p
                        variants={heroItemVariants}
                        className="mt-3 max-w-md text-lg text-indigo-100 sm:text-xl md:mt-5 md:max-w-3xl mb-10 w-full md:w-11/12 lg:w-8/12"
                    >
                        Streamlining blockchain integration by removing legacy barriers, cutting costs, and improving compliance.
                    </motion.p>

                    <motion.div
                        variants={heroItemVariants}
                        // MODIFIED: gap-4 changed to gap-2 for tighter spacing on mobile
                        className="mt-5 sm:mt-8 flex flex-row flex-wrap gap-2 justify-start items-center"
                    >
                        <Link
                            to="/request-demo"
                            // MODIFIED: px-8 changed to px-6 to reduce button width
                            className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#3D29A1] text-base font-medium rounded-md shadow-sm text-white bg-[#3D29A1] hover:text-white hover:bg-transparent hover:border-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-purple-500"
                        >
                            Request Demo
                        </Link>
                        <Link
                            to="/solutions"
                            type="button"
                            // MODIFIED: px-8 changed to px-6 to reduce button width
                            className="inline-flex items-center justify-center px-6 py-3 border border-white hover:border-[#3D29A1] text-base font-medium rounded-md shadow-sm text-white bg-transparent hover:bg-[#3D29A1] hover:text-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-purple-500"
                        >
                            Explore Features
                        </Link>
                    </motion.div>
                </motion.div>
            </div>

            <LogoScroller />


            {/* Partnership  */}
            <div className="py-12 bg-black">
                <PartnerSection />
                {/* About Us Section */}
                <AnimatedSection
                    triggerOnce={false}
                    animationVariants={fadeIn}
                    className='flex flex-col lg:flex-row w-11/12 md:w-10/12 lg:w-9/12 mx-auto items-start lg:items-center gap-10 md:gap-16 my-16 md:my-24 text-left'
                >
                    <AnimatedSection triggerOnce={false} tag="div" animationVariants={fadeInLeft} className='text-white w-full lg:w-6/12 space-y-6 md:space-y-8'>
                        <p className='w-fit inline-block border border-[#817AF7] text-[#817AF7] rounded-full px-4 py-1 text-sm font-medium'>About Us</p>
                        <h2 className="text-4xl md:text-5xl text-white mt-4">Reimagining Infrastructure for the Decentralized Era</h2>
                        <p className='text-[#AAA8A8] text-lg md:text-xl'>
                            At ZenithBlox, we’re bridging the world of traditional finance with the promise of a decentralized future. Our mission is to empower financial institutions to evolve — unlocking new revenue streams, improving operational efficiency, and enhancing trust through secure, scalable blockchain integration. With a focus on usability and real-world impact, we help organizations transition with confidence into the Web3 era.
                        </p>
                    </AnimatedSection>
                    <AnimatedSection triggerOnce={false} tag="div" animationVariants={fadeInRight} className='w-full lg:w-6/12 flex flex-col items-start lg:items-end mt-10 lg:mt-0'>
                        <Link
                            to="/about-us"
                            className="w-auto inline-flex items-center justify-center px-8 py-3 border border-white hover:border-[#3D29A1] text-base font-medium rounded-md shadow-sm text-white bg-transparent hover:bg-[#3D29A1] hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-purple-500 transition-colors duration-150 mb-10 md:mb-12"
                        >
                            Read More
                        </Link>
                        <motion.img
                            variants={scaleUp}
                            className="w-auto h-[250px] sm:h-[300px] md:h-[350px] lg:h-[400px]"
                            src={glows}
                            alt="Abstract glowing graphic"
                        />
                    </AnimatedSection>
                </AnimatedSection>
            </div>


            {/* Solutions Section */}
            <div className='bg-gray-100 py-16 md:py-24'>
                <div className='w-11/12 md:w-10/12 mx-auto'>
                    <AnimatedSection
                        triggerOnce={false}
                        animationVariants={fadeInUp}
                        className='flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12 md:mb-16 px-4 text-left'
                    >
                        <div className='text-black w-full lg:w-7/12 space-y-4 md:space-y-6'>
                            <p className='w-fit inline-block border border-[#817AF7] text-[#817AF7] rounded-full px-4 py-1 text-sm font-medium'>
                                Solutions
                            </p>
                            <h2 className="text-4xl sm:text-5xl md:text-6xl text-black mt-4">
                                Solutions Built for the Future of Finance
                            </h2>
                        </div>
                        <div className='w-full lg:w-auto flex justify-start lg:justify-end items-end mt-6 lg:mt-0'>
                            <Link to="/solutions"
                                className='bg-transparent border border-black text-black py-3 px-8 rounded-lg text-base font-medium hover:bg-black hover:text-white transition-colors duration-150'>
                                Explore all solutions →
                            </Link>
                        </div>
                    </AnimatedSection>

                    <AnimatedSection
                        triggerOnce={false}
                        animationVariants={fadeIn}
                        staggerChildren={0.15}
                        className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 text-white'
                    >
                        {solutionCards.map((item, index) => (
                            <motion.div
                                key={index}
                                variants={staggerItemFadeInUp}
                                className='rounded-2xl p-6 flex flex-col space-y-3
                                        bg-black border border-white/20 shadow-lg
                                        hover:border-white/40 hover:shadow-purple-500/30 transition-all duration-300 text-left'
                                whileHover={{
                                    scale: 1.04,
                                    y: -6
                                }}
                                transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 15
                                }}
                            >
                                <img
                                    className="w-10 h-10 sm:w-12 sm:h-12 mb-3"
                                    src={item.icon}
                                    alt={`${item.title} icon`}
                                />
                                <h3 className='font-semibold text-xl sm:text-2xl text-white'>
                                    <span dangerouslySetInnerHTML={{ __html: item.title }} />
                                </h3>
                                <p className='text-sm sm:text-lg text-[#B4B4B4] flex-grow'>
                                    <span dangerouslySetInnerHTML={{ __html: item.desc }} />
                                </p>
                                <Link
                                    to={item.linkTo}
                                    className='mt-auto bg-white/15 backdrop-blur-md border border-white/25 text-white font-medium py-3 px-6 rounded-lg w-full block text-center
                                            transition-all duration-300 ease-in-out hover:bg-white/30 hover:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/40'
                                    role="button"
                                >
                                    Explore →
                                </Link>
                            </motion.div>
                        ))}
                    </AnimatedSection>
                </div>
            </div>

            {/* Ready to Build the Future? Section */}
            <div className='py-16 md:py-24 bg-black'>
                <AnimatedSection
                    triggerOnce={false}
                    animationVariants={fadeIn}
                    className="container bg-[#131313] mx-auto px-6 sm:px-12 w-11/12 md:w-10/12 py-16 md:py-24 rounded-3xl flex flex-col lg:flex-row items-start lg:items-center gap-10 md:gap-16 text-left"
                >
                    <AnimatedSection triggerOnce={false} tag="div" animationVariants={fadeInLeft} className="w-full lg:w-3/5 lg:pr-16 space-y-6 md:space-y-8">
                        <h2 className='text-white text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight'>
                            Ready to Build the Future?
                        </h2>
                        <p className='text-[#AAA8A8] text-lg md:text-xl'>
                            Let’s explore how ZenithBlox can help your organization integrate blockchain with confidence and clarity.
                        </p>
                        <Link
                            to="/request-demo"
                            className="inline-flex items-center justify-center px-8 py-3 border-2 border-[#3D29A1] text-base font-medium rounded-md shadow-sm text-white bg-[#3D29A1] hover:text-white hover:bg-transparent hover:border-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-purple-500"
                        >
                            Request Demo
                        </Link>
                    </AnimatedSection>
                    <AnimatedSection triggerOnce={false} tag="div" animationVariants={scaleUp} className="w-full lg:w-2/5 mt-12 lg:mt-0 flex justify-start lg:justify-end items-center">
                        <img
                            src={World}
                            alt="Global Blockchain Integration illustration"
                            className="rounded-xl max-w-full h-auto object-cover md:max-h-[300px] lg:max-h-[350px]"
                        />
                    </AnimatedSection>
                </AnimatedSection>
            </div>

            {/* Usecase and ContactForm Sections */}
            <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className='py-16 md:py-24 bg-white'>
                <Usecase />
            </AnimatedSection>
            <FaqSection />

            <ContactForm />
        </>
    );
}

export default HomePage;
